import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { X, Pizza, Loader2, Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { MenuItem, MenuItemOption } from '../../lib/types';
import { supabase } from '../../lib/supabase';
import { formatCurrency, cn } from '../../lib/utils';
interface EmiliusArmaTuPizzaModalProps {
  isOpen: boolean;
  onClose: () => void;
  // IMPORTANT: argument order MUST match the host's handleAddToCart on
  // RestaurantPage / MenuPage / PublicMenuPage:
  //   (item, quantity, options, notes)
  // The previous signature swapped notes/options which crashed the cart
  // (it tried to .map() over a string instead of an options array,
  // blanking the page after "Agregar al pedido"). Do NOT change this
  // order without updating every host.
  onAdd: (item: Omit<MenuItem, 'id'> & {
    id?: string;
  }, quantity: number, selectedOptions: any[], notes: string) => void;
  pizzas: MenuItem[];
}
// Tamaños disponibles para "Armá tu propia pizza". Mantenemos `key` como
// el matcher canónico (lowercase, sin tildes) — se usa para buscar el
// precio en menu_item_options y para la regla de Chiniadita. `label` es
// lo que ve el cliente y lo que se imprime en el ticket, ya con las
// porciones que pidió el dueño. Si el chart del menú impreso cambia,
// se actualiza acá Y en UPDATE_EMILIUS_PIZZA_SIZE_PORCIONES.sql.
const SIZES: {
  key: string;
  label: string;
}[] = [{
  key: 'personal',
  label: 'Personal (6 porciones)'
}, {
  key: 'mediana',
  label: 'Mediana (6 porciones)'
}, {
  key: 'grande',
  label: 'Grande (8 porciones)'
}, {
  key: 'super',
  label: 'Super (12 porciones)'
}, {
  key: '18',
  label: '18" (16 porciones)'
}];
// Items that live in the Emilius pizza category by name but are NOT
// actual pizza flavors — they're pizza ADD-ONS (extra queso, borde
// relleno). They must never appear as "mitad" choices here; they're
// offered separately as a checkout upsell with size-aware pricing.
const PIZZA_ADDON_NAMES = ['extra queso', 'borde relleno'];
function isPizzaAddon(name: string): boolean {
  const n = (name || '').toLowerCase().trim();
  return PIZZA_ADDON_NAMES.some(addon => n.includes(addon));
}
// ── Chiniadita — pizza ESPECIAL con reglas de negocio propias ──
// Solo se permite como opción de mitad-y-mitad cuando el tamaño elegido
// es grande, super o 18". En personal/mediana no se puede combinar.
// Estas reglas también viven en EmiliusCheckoutUpsellModal (donde se
// excluye de los add-ons de queso/borde).
const CHINIADITA_ALLOWED_SIZES = ['grande', 'super', '18', '18"'];
function isChiniadita(name: string): boolean {
  return (name || '').toLowerCase().includes('chiniadita');
}
function isSizeAllowedForChiniadita(size: string): boolean {
  const s = (size || '').toLowerCase().trim();
  return CHINIADITA_ALLOWED_SIZES.some(allowed => s.includes(allowed));
}
// "5 Estaciones" tiene su propio flujo (pickear 5 sabores), no encaja
// como mitad-y-mitad — la excluimos del armado.
function is5Estaciones(name: string): boolean {
  const n = (name || '').toLowerCase();
  return n.includes('5 estaciones') || n.includes('cinco estaciones');
}
// Normaliza texto para comparaciones robustas: minúsculas + SIN TILDES.
// Crítico para los tamaños: el `key` del front es "super" (sin tilde)
// pero en la BD la opción se llama "Súper (12 porciones)". Sin esta
// normalización "súper...".includes("super") da FALSE y el precio de la
// mitad caía al precio base (sin el delta del tamaño) → se cobraba de
// menos. Con normalize() ambos lados quedan como "super" y matchea.
function normalize(s: string): string {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}
// ¿Es el grupo de opciones un grupo de TAMAÑO? (Tamaño / Tamano / Size)
// Mismo criterio que UPDATE_EMILIUS_PIZZA_SIZE_PORCIONES.sql
// (option_group ILIKE '%tama%' OR '%size%'). Antes el fetch usaba
// .ilike('option_group','tamaño') que es match EXACTO y se perdía
// cualquier variante sin tilde o en inglés → delta 0 → cobro errado.
function isSizeGroup(group: string): boolean {
  const g = normalize(group);
  return g.includes('tama') || g.includes('size');
}
export function EmiliusArmaTuPizzaModal({
  isOpen,
  onClose,
  onAdd,
  pizzas
}: EmiliusArmaTuPizzaModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  // Defensive filter — even if the parent passes addons in, we never
  // render them as flavor choices. Also exclude 5 Estaciones (it has
  // its own dedicated modal).
  const baseFlavorPizzas = useMemo(() => pizzas.filter(p => !isPizzaAddon(p.name) && !is5Estaciones(p.name)), [pizzas]);
  // Chiniadita: solo aparece como mitad cuando el tamaño elegido lo
  // permite. Si el cliente eligió personal o mediana, la sacamos del
  // listado para que NO PUEDA seleccionarla — la regla es de negocio,
  // no estética.
  const flavorPizzas = useMemo(() => {
    if (!selectedSize) return baseFlavorPizzas;
    if (isSizeAllowedForChiniadita(selectedSize)) return baseFlavorPizzas;
    return baseFlavorPizzas.filter(p => !isChiniadita(p.name));
  }, [baseFlavorPizzas, selectedSize]);
  const [flavor1, setFlavor1] = useState<MenuItem | null>(null);
  const [flavor2, setFlavor2] = useState<MenuItem | null>(null);
  const [optionsMap, setOptionsMap] = useState<Record<string, MenuItemOption[]>>({});
  const [loadingOptions, setLoadingOptions] = useState(false);
  // Fetch options for all pizzas so we know the price deltas for the sizes
  useEffect(() => {
    if (!isOpen || flavorPizzas.length === 0) return;
    const fetchAllOptions = async () => {
      setLoadingOptions(true);
      try {
        const pizzaIds = flavorPizzas.map(p => p.id);
        // Traemos TODAS las opciones de cada pizza y filtramos por grupo
        // de tamaño en el cliente (tolerante a tildes / inglés). Así
        // nunca perdemos el delta del tamaño por un nombre de grupo
        // ligeramente distinto.
        const {
          data,
          error
        } = await supabase.from('menu_item_options').select('*').in('menu_item_id', pizzaIds);
        if (error) throw error;
        const map: Record<string, MenuItemOption[]> = {};
        for (const opt of data || []) {
          if (!isSizeGroup(opt.option_group || '')) continue;
          if (!map[opt.menu_item_id]) map[opt.menu_item_id] = [];
          map[opt.menu_item_id].push(opt);
        }
        setOptionsMap(map);
      } catch (err) {
        console.error('[ArmaTuPizza] Error fetching options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    fetchAllOptions();
  }, [isOpen, pizzas]);
  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedSize('');
      setFlavor1(null);
      setFlavor2(null);
    }
  }, [isOpen]);
  // Si el cliente cambia el tamaño después de elegir Chiniadita y el
  // nuevo tamaño no la permite, limpiamos la selección para no quedar
  // en un estado inconsistente.
  useEffect(() => {
    if (!selectedSize) return;
    if (isSizeAllowedForChiniadita(selectedSize)) return;
    if (flavor1 && isChiniadita(flavor1.name)) setFlavor1(null);
    if (flavor2 && isChiniadita(flavor2.name)) setFlavor2(null);
  }, [selectedSize, flavor1, flavor2]);
  // Calculate price for a specific flavor at the selected size.
  // Uses substring matching against the canonical `key` (e.g. "grande")
  // because the DB option names now include the porciones suffix
  // ("Grande (8 porciones)") — exact equality would silently fall back
  // to delta=0 and undercharge. Mirrors priceForSize() in
  // EmiliusCheckoutUpsellModal so both flows stay consistent.
  // Matriz de precios: (sabor, tamaño) → precio de ESA pizza en ESE
  // tamaño = precio base + delta del tamaño correspondiente.
  // El matching de tamaño es SIN TILDES (normalize) para que "super"
  // matchee "Súper (12 porciones)". Esto construye, en efecto, la tabla
  // pizza_name + size_name = price que pidió el dueño.
  const getPriceForFlavor = (flavor: MenuItem | null, size: string) => {
    if (!flavor || !size) return 0;
    const basePrice = Number(flavor.price) || 0;
    const opts = optionsMap[flavor.id] || [];
    const sizeKey = normalize(size); // 'super' | 'grande' | 'mediana'
    const sizeOpt = opts.find(o => normalize(o.name || '').includes(sizeKey));
    const delta = sizeOpt ? Number(sizeOpt.price_delta) || 0 : 0;
    return basePrice + delta;
  };
  const price1 = getPriceForFlavor(flavor1, selectedSize);
  const price2 = getPriceForFlavor(flavor2, selectedSize);
  // SIEMPRE se cobra la mitad MÁS CARA según el tamaño elegido.
  // Nunca se suman ni se promedian las dos mitades.
  const finalPrice = Math.max(price1, price2);
  const handleNext = () => {
    if (step === 1 && selectedSize) setStep(2);else if (step === 2 && flavor1) setStep(3);
  };
  const handleBack = () => {
    if (step === 3) setStep(2);else if (step === 2) setStep(1);
  };
  const handleConfirm = () => {
    // ── BLINDAJE: every path is wrapped so a missing field, a
    // misbehaving host onAdd, or a bad price NEVER blanks the page.
    try {
      if (!selectedSize || !flavor1 || !flavor2) return;
      // Precio final = la mitad MÁS CARA según el tamaño (price1/price2 ya
      // incluyen base + delta del tamaño). Nunca sumamos ni promediamos.
      // Sólo si por algún motivo las opciones no cargaron y el cálculo da
      // 0, caemos al mayor de los precios base como red de seguridad (así
      // no se cobra ₡0), pero NO clampeamos hacia arriba cuando ya hay un
      // precio de tamaño válido — eso podría sobrecobrar.
      const safeBase1 = Number(flavor1.price) || 0;
      const safeBase2 = Number(flavor2.price) || 0;
      const sizeBasedMax = Math.max(Number(price1) || 0, Number(price2) || 0);
      const computedPrice = sizeBasedMax > 0 ? sizeBasedMax : Math.max(safeBase1, safeBase2);
      // Create a synthetic menu item for the cart. Pattern matches the
      // existing "Arma tu plato" items in the codebase: menu_item_id is
      // left undefined → coerced to null by create-order.ts so it
      // doesn't trip the menu_items FK constraint.
      const syntheticItem = {
        restaurant_id: flavor1.restaurant_id,
        category_id: flavor1.category_id,
        name: 'Armá tu propia pizza',
        description: `Mitad ${flavor1.name} / Mitad ${flavor2.name}`,
        price: computedPrice,
        is_active: true,
        image_url: null,
        // Route the synthetic line to the same station as a real pizza
        // (pizzeria) so the KDS ticket lands in cocina/pizzería. Falls
        // back to flavor2 then null so we never blow up.
        station_code: (flavor1 as any).station_code || (flavor2 as any).station_code || null
      };
      // The size is added as a selected option so it prints on the ticket.
      // Use the full label (con porciones) para que la cocina vea el
      // mismo texto que el cliente eligió en pantalla.
      const sizeMeta = SIZES.find(s => s.key === selectedSize);
      const sizeLabel = sizeMeta?.label || selectedSize.charAt(0).toUpperCase() + selectedSize.slice(1);
      const sizeOption = {
        option_name: sizeLabel,
        option_group: 'Tamaño',
        price_delta: 0 // Price is already baked into the base price of the synthetic item
      };
      // ── ORDER OF ARGS MATTERS — matches handleAddToCart everywhere:
      //   (item, quantity, options, notes)
      onAdd(syntheticItem, 1, [sizeOption], `Mitad 1: ${flavor1.name}\nMitad 2: ${flavor2.name}`);
      onClose();
    } catch (err) {
      console.error('[EmiliusArmaTuPizza] handleConfirm failed:', err);
      // Surface a soft message instead of crashing the app. The modal
      // stays open so the user can try again or back out.
      // eslint-disable-next-line no-alert
      alert('No se pudo agregar la pizza al pedido. Volvé a intentar o tocá Atrás para revisar las selecciones.');
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} maxWidth="md" data-id="element-2318" data-mp="fjdm3vk7utgl">
      <div className="bg-[#0B0B0B] text-white overflow-hidden flex flex-col max-h-[85vh]" data-id="element-2319" data-mp="alvgof5eqwdc">
        {/* Header */}
        <div className="shrink-0 px-4 py-4 border-b border-white/10 flex items-center justify-between bg-[#111]" data-id="element-2320" data-mp="3j0neq4ge8j2n">
          <div data-id="element-2321" data-mp="3f154c2m77qe2">
            <h2 className="text-lg font-black text-[#6FA248] uppercase tracking-wider" data-id="element-2322" data-mp="3f1enb68tsxa9">
              Armá tu propia pizza
            </h2>
            <p className="text-xs text-white/50 mt-0.5" data-id="element-2323" data-mp="3o0rv7uhaxdsv">
              Mitad y mitad — se cobra el valor de la especialidad mayor
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-white/70 hover:bg-white/10 hover:text-white transition-colors" data-id="element-2324" data-mp="187j5oezjaceu">
            <X className="w-5 h-5" data-id="element-2325" data-mp="2la8v5sbjrj87" />
          </button>
        </div>

        {/* Progress */}
        <div className="shrink-0 flex items-center px-4 py-3 bg-[#161616] border-b border-white/5" data-id="element-2326" data-mp="2q4wb32shj5b6">
          {[1, 2, 3].map(s => <Fragment key={s} data-id="element-2327" data-mp="2h5sovpy0u9sg">
              <div className={cn('w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors', step === s ? 'bg-[#6FA248] text-white shadow-[0_0_10px_rgba(111,162,72,0.3)]' : step > s ? 'bg-[#6FA248]/30 text-[#6FA248]' : 'bg-white/5 text-white/30')} data-id="element-2328" data-mp="2h73u7u747plo">
                {step > s ? <Check className="w-3 h-3" data-id="element-2329" data-mp="3nu7oxoh42jl2" /> : s}
              </div>
              {s < 3 && <div className={cn('flex-1 h-0.5 mx-2 transition-colors', step > s ? 'bg-[#6FA248]/30' : 'bg-white/5')} data-id="element-2330" data-mp="3slzk6cb7pdhj" />}
            </Fragment>)}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar" data-id="element-2331" data-mp="1uygjko1whvyn">
          {loadingOptions ? <div className="flex flex-col items-center justify-center py-12 text-white/50" data-id="element-2332" data-mp="1q0ne5x8f95zf">
              <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#6FA248]" data-id="element-2333" data-mp="2mqnxvhtxow9v" />
              <p className="text-sm" data-id="element-2334" data-mp="2ioj7qt2p2hrz">Cargando opciones...</p>
            </div> : <>
              {step === 1 && <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2335" data-mp="2dwsw8js0tnyz">
                  <h3 className="text-sm font-bold text-white/90 mb-4" data-id="element-2336" data-mp="28xohzeq8p01s">
                    1. Seleccioná el tamaño
                  </h3>
                  {SIZES.map(size => <button key={size.key} onClick={() => setSelectedSize(size.key)} className={cn('w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all', selectedSize === size.key ? 'border-[#6FA248] bg-[#6FA248]/10' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-2337" data-mp="31n59ma61ccwy">
                      <span className="text-base font-bold" data-id="element-2338" data-mp="3rwe6iluqhinb">{size.label}</span>
                      <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center', selectedSize === size.key ? 'border-[#6FA248] bg-[#6FA248]' : 'border-white/20')} data-id="element-2339" data-mp="ql0xk4kas2u">
                        {selectedSize === size.key && <div className="w-2 h-2 rounded-full bg-white" data-id="element-2340" data-mp="jidxfoiuavfb" />}
                      </div>
                    </button>)}
                </div>}

              {step === 2 && <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2341" data-mp="2pajzsfxn12wg">
                  <h3 className="text-sm font-bold text-white/90 mb-4" data-id="element-2342" data-mp="2kjtvz74v183v">
                    2. Elegí la primera mitad
                  </h3>
                  <div className="grid grid-cols-1 gap-2" data-id="element-2343" data-mp="2z7or0u6cbok4">
                    {flavorPizzas.map(pizza => {
                const isSelected = flavor1?.id === pizza.id;
                return <button key={pizza.id} onClick={() => setFlavor1(pizza)} className={cn('w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all', isSelected ? 'border-[#6FA248] bg-[#6FA248]/10' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-2344" data-mp="3884nvkskvxg0">
                          <div className="w-12 h-12 rounded-lg bg-black overflow-hidden shrink-0" data-id="element-2345" data-mp="3pkuxazrte7h6">
                            {pizza.image_url ? <img src={pizza.image_url} alt={pizza.name} className="w-full h-full object-cover opacity-80" data-id="element-2346" data-mp="3frvt49ju48cg" /> : <div className="w-full h-full flex items-center justify-center bg-white/5" data-id="element-2347" data-mp="2sszpxt3ncayy">
                                <Pizza className="w-5 h-5 text-white/20" data-id="element-2348" data-mp="2wtb7uy4mjx6q" />
                              </div>}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2349" data-mp="v2pw7d8s7axs">
                            <p className="font-bold text-sm truncate text-white/90" data-id="element-2350" data-mp="v26iy9u2taq3">
                              {pizza.name}
                            </p>
                          </div>
                          <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0', isSelected ? 'border-[#6FA248] bg-[#6FA248]' : 'border-white/20')} data-id="element-2351" data-mp="3a060jy1hl604">
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" data-id="element-2352" data-mp="2ma3d5v8ioo7u" />}
                          </div>
                        </button>;
              })}
                  </div>
                </div>}

              {step === 3 && <div className="space-y-3 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-2353" data-mp="fjvh9gyfhlgl">
                  <h3 className="text-sm font-bold text-white/90 mb-4" data-id="element-2354" data-mp="o0vzu7dg68wz">
                    3. Elegí la segunda mitad
                  </h3>
                  <div className="grid grid-cols-1 gap-2" data-id="element-2355" data-mp="s0wosly6a2ia">
                    {flavorPizzas.map(pizza => {
                const isSelected = flavor2?.id === pizza.id;
                return <button key={pizza.id} onClick={() => setFlavor2(pizza)} className={cn('w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all', isSelected ? 'border-[#6FA248] bg-[#6FA248]/10' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-2356" data-mp="11r9a4gdt9zph">
                          <div className="w-12 h-12 rounded-lg bg-black overflow-hidden shrink-0" data-id="element-2357" data-mp="1ugpzf564x0hh">
                            {pizza.image_url ? <img src={pizza.image_url} alt={pizza.name} className="w-full h-full object-cover opacity-80" data-id="element-2358" data-mp="36aqik4ga6htp" /> : <div className="w-full h-full flex items-center justify-center bg-white/5" data-id="element-2359" data-mp="3kze89u3g3xbq">
                                <Pizza className="w-5 h-5 text-white/20" data-id="element-2360" data-mp="2ndp7c1w4vlhb" />
                              </div>}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2361" data-mp="2xabtqv43yklg">
                            <p className="font-bold text-sm truncate text-white/90" data-id="element-2362" data-mp="2xcy0wtn36y35">
                              {pizza.name}
                            </p>
                          </div>
                          <div className={cn('w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0', isSelected ? 'border-[#6FA248] bg-[#6FA248]' : 'border-white/20')} data-id="element-2363" data-mp="ckcowuid0rn2">
                            {isSelected && <div className="w-2 h-2 rounded-full bg-white" data-id="element-2364" data-mp="1nddet3x9ysz2" />}
                          </div>
                        </button>;
              })}
                  </div>
                </div>}
            </>}
        </div>

        {/* Footer */}
        <div className="shrink-0 p-4 border-t border-white/10 bg-[#111]" data-id="element-2365" data-mp="19dvm4dguyvkf">
          {step === 3 && flavor1 && flavor2 ? <div className="mb-4 p-3 rounded-xl bg-white/5 border border-white/10" data-id="element-2366" data-mp="14g2hhov7e9m9">
              <div className="flex justify-between items-center mb-2" data-id="element-2367" data-mp="33qlhz0qseeug">
                <span className="text-xs text-white/50 uppercase tracking-wider font-bold" data-id="element-2368" data-mp="2yt21du6m9pzc">
                  Resumen
                </span>
                <span className="text-[10px] font-bold bg-[#6FA248]/20 text-[#6FA248] px-2 py-0.5 rounded" data-id="element-2369" data-mp="3huj24k7yin1u">
                  {SIZES.find(s => s.key === selectedSize)?.label || `Tamaño ${selectedSize}`}
                </span>
              </div>
              <div className="flex items-center text-sm mb-1" data-id="element-2370" data-mp="3dvtpwuynnbjs">
                <span className="text-white/80 truncate" data-id="element-2371" data-mp="5fi6j0n5onlu">½ {flavor1.name}</span>
              </div>
              <div className="flex items-center text-sm mb-3" data-id="element-2372" data-mp="9gk5rhh2oznt">
                <span className="text-white/80 truncate" data-id="element-2373" data-mp="9epvcx3fgpxz">½ {flavor2.name}</span>
              </div>
              <div className="h-px bg-white/10 w-full mb-3" data-id="element-2374" data-mp="3roje961nosk2" />
              <div className="flex justify-between items-end" data-id="element-2375" data-mp="f2uccem1m5g">
                <span className="text-xs text-white/50" data-id="element-2376" data-mp="2g7jmqcn4uhr3">Total a cobrar</span>
                <span className="text-xl font-black text-[#6FA248] tabular-nums" data-id="element-2377" data-mp="2g9cs7om0evxi">
                  {formatCurrency(finalPrice)}
                </span>
              </div>
            </div> : null}

          <div className="flex gap-3" data-id="element-2378" data-mp="2doonq83pa538">
            {step > 1 && <Button variant="outline" onClick={handleBack} className="flex-1 border-white/20 text-white hover:bg-white/10" data-id="element-2379" data-mp="28n8ys4yejefk">
                Atrás
              </Button>}
            {step < 3 ? <Button onClick={handleNext} disabled={step === 1 && !selectedSize || step === 2 && !flavor1} className="flex-[2] bg-[#6FA248] hover:bg-[#5c873b] text-white font-bold" data-id="element-2380" data-mp="1tzwdmhcibapp">
                Siguiente
              </Button> : <Button onClick={handleConfirm} disabled={!flavor2} className="flex-[2] bg-[#6FA248] hover:bg-[#5c873b] text-white font-bold" data-id="element-2381" data-mp="1yw4lrasae3nd">
                Agregar al pedido
              </Button>}
          </div>
        </div>
      </div>
    </Modal>;
}